export interface MediaMetadata {
  id: string;
  mediaId: string;
  ownerId: string;
  width?: number;
  height?: number;
  format?: string;
  fileSize?: number;
  colorSpace?: string;
  compression?: string;
  orientation?: number;
  takenAt?: string;
  deviceModel?: string;
  deviceSerialNumber?: string;
  uploadDeviceModel?: string;
  uploadDevicePlatform?: string;
  uploadDevicePlatformVersion?: string;
  uploadUserAgent?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  version: number;
}

export interface AlbumMediaUploader {
  /** Stable upload-source identity (`userId:deviceKey`), later mergeable. */
  id: string;
  userId: string;
  deviceKey: string;
  uploadedByEmail?: string;
  deviceModel?: string;
  deviceSerialNumber?: string;
  uploadDeviceModel?: string;
  uploadDevicePlatform?: string;
}

export type MediaUploaderFilter = string[];

export function mediaUploaderFilterKey(sourceIds?: string[] | null): string {
  if (!sourceIds || sourceIds.length === 0) {
    return "all";
  }
  return [...sourceIds].sort().join("\n");
}

export function toggleAlbumUploaderFilter(
  selectedIds: string[],
  toggledId: string | null,
  allIds: string[]
): string[] {
  if (toggledId == null) {
    return [];
  }
  const next = new Set(selectedIds);
  if (next.has(toggledId)) {
    next.delete(toggledId);
  } else {
    next.add(toggledId);
  }
  if (next.size === 0 || (allIds.length > 0 && allIds.every((id) => next.has(id)))) {
    return [];
  }
  return allIds.filter((id) => next.has(id));
}

export function albumMediaUploaderFilterLabel(
  uploaders: AlbumMediaUploader[],
  selectedIds: string[],
  currentUserId?: string | null
): string {
  if (selectedIds.length === 0) {
    return "All uploaders";
  }
  const selected = uploaders.filter((uploader) => selectedIds.includes(uploader.id));
  if (selected.length === 0) {
    return "All uploaders";
  }
  if (selected.length === 1) {
    return albumMediaUploaderLabel(selected[0], currentUserId);
  }
  if (selected.length === 2) {
    return selected.map((uploader) => albumMediaUploaderLabel(uploader, currentUserId)).join(", ");
  }
  return `${selected.length} uploaders`;
}

export function albumMediaUploaderDeviceLabel(uploader: AlbumMediaUploader): string | undefined {
  const model = uploader.uploadDeviceModel?.trim() || uploader.deviceModel?.trim();
  const serial = uploader.deviceSerialNumber?.trim();
  if (model && serial) {
    return `${model} (${serial})`;
  }
  if (model) {
    return model;
  }
  if (serial) {
    return serial;
  }
  const platform = uploader.uploadDevicePlatform?.trim();
  return platform || undefined;
}

export function albumMediaUploaderLabel(
  uploader: AlbumMediaUploader,
  currentUserId?: string | null
): string {
  const who =
    currentUserId && uploader.userId === currentUserId
      ? "You"
      : uploader.uploadedByEmail?.trim() || undefined;
  const device = albumMediaUploaderDeviceLabel(uploader);
  if (who && device) {
    return `${who} · ${device}`;
  }
  if (who) {
    return who;
  }
  if (device) {
    return device;
  }
  return "Unknown";
}

export function isAlbumUploaderId(value: string | null | undefined): value is string {
  return (
    !!value &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
  );
}

export interface Media {
  id: string;
  albumId: string;
  ownerId: string;
  userId: string;
  uploadedByEmail?: string;
  filename: string;
  contentType: string;
  /**
   * Size in bytes of the original uploaded file (DB column: `original_size`).
   * Not always present on every API response shape, so keep optional.
   */
  originalSize?: number;
  metadata: Partial<MediaMetadata>;
  url?: string;
  originalUrl?: string;
  playbackUrl?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  version: number;
}

export interface MediaIndexItem {
  id: string;
  userId: string;
  createdAt: string;
  contentType: string;
  filename: string;
  takenAt?: string;
}

export interface MediaIndex {
  items: MediaIndexItem[];
}

export interface MultipleMediaUploadResult {
  successful: Media[];
  failed: { filename: string; error: string }[];
  duplicates?: { filename: string }[];
  totalUploaded: number;
  totalFailed: number;
  totalDuplicates?: number;
}

export interface MediaDuplicateFingerprint {
  filename: string;
  originalSize: number;
  takenAt?: string;
}

export interface MediaDuplicateFingerprints {
  items: MediaDuplicateFingerprint[];
}

export interface MediaUploadFile {
  file: File;
  preview?: string;
}

export type MediaUploadPrepareStatus =
  | { phase: "idle" }
  | { phase: "waiting-for-files" }
  | { phase: "processing"; processed: number; total: number };

export interface MediaListResponse {
  media: Media[];
  total: number;
  offset: number;
  limit: number;
}

export interface MediaLike {
  id: string;
  ownerId: string;
  albumId: string;
  mediaId: string;
  userId: string;
  userEmail: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  version: number;
}

export interface MediaLikesResponse {
  likes: MediaLike[];
  total: number;
  offset: number;
  limit: number;
}

export interface MediaComment {
  id: string;
  ownerId: string;
  albumId: string;
  mediaId: string;
  userId: string;
  userEmail: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  version: number;
}

export interface MediaCommentsResponse {
  comments: MediaComment[];
  total: number;
  offset: number;
  limit: number;
}

export interface CreateMediaCommentDto {
  content: string;
}

export interface UpdateMediaCommentDto {
  content?: string;
}
